import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ErrorHandler,
  computed,
  inject,
  input,
  output
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { map } from 'rxjs';
import { UtilityService } from '../services/utility.service';

type ErrorPageKind = 'session-ended' | 'not-found' | 'unavailable';

const COPY: Record<
  ErrorPageKind,
  { icon: string; title: string; description: string }
> = {
  'session-ended': {
    icon: 'lock_clock',
    title: 'ERRORS.PAGE.SESSION_ENDED_TITLE',
    description: 'ERRORS.PAGE.SESSION_ENDED_DESC'
  },
  'not-found': {
    icon: 'search_off',
    title: 'ERRORS.PAGE.NOT_FOUND_TITLE',
    description: 'ERRORS.PAGE.NOT_FOUND_DESC'
  },
  unavailable: {
    icon: 'cloud_off',
    title: 'ERRORS.PAGE.UNAVAILABLE_TITLE',
    description: 'ERRORS.PAGE.UNAVAILABLE_DESC'
  }
};

/**
 * The whole page is unavailable: a quiet full-area state. On the `/error`
 * route it reads `status` from the URL; inside a page the host passes
 * `status` and handles `retry`. The status picks the copy but never shows:
 * a code means nothing to the people reading the page.
 */
@Component({
  selector: 'app-error-page',
  templateUrl: './error-page.component.html',
  styleUrls: ['./error-page.component.scss'],
  imports: [ButtonModule, TranslocoPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-route]': 'isRoute()',
    '[class.is-embedded]': 'isEmbedded'
  }
})
export class ErrorPageComponent implements AfterViewChecked {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly errorHandler = inject(ErrorHandler);
  private readonly utilityService = inject(UtilityService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  /** The failed request's status; 0 when there was no response. */
  readonly status = input<number>();
  readonly retry = output<void>();

  readonly isEmbedded = window.parent !== window;
  readonly isRoute = computed(() => this.status() === undefined);

  private readonly routeStatus = toSignal(
    this.route.queryParamMap.pipe(
      map((params) => Number(params.get('status')) || undefined)
    )
  );

  readonly shownStatus = computed(() =>
    this.isRoute() ? this.routeStatus() : this.status()
  );

  readonly kind = computed<ErrorPageKind>(() => {
    const status = this.shownStatus();
    if (status === 401) return 'session-ended';
    // The route without a status is the fallback for unknown paths.
    if (status === 404 || (this.isRoute() && !status)) return 'not-found';
    return 'unavailable';
  });

  readonly copy = computed(() => COPY[this.kind()]);

  ngAfterViewChecked() {
    // Inside a page the layout reports the height; on its own route this does.
    if (this.isRoute()) {
      this.utilityService.emitPropToParent(
        'contentHeight',
        this.elementRef.nativeElement.offsetHeight / 14
      );
    }
  }

  onTryAgain() {
    if (this.isRoute()) {
      this.goToDashboard();
    } else {
      this.retry.emit();
    }
  }

  goToDashboard() {
    this.router.navigate(['/']).catch((error: unknown) => {
      this.errorHandler.handleError(error);
    });
  }
}
